import { useEffect, useMemo, useState } from 'react';
import { LottieAnimation } from './LottieMedia.types';
import { buildProxyUrl, isLottiePayload } from './LottieMedia.utils';

const isValidLottiePayload = (data: unknown): object | null => {
  try {
    if (!isLottiePayload(data)) {
      throw new Error('LottieMedia received malformed Lottie animation data');
    }

    return data;
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(error);
    return null;
  }
};

export function useLottieAnimation(animation: LottieAnimation) {
  const animationUrl = typeof animation === 'string' ? animation : undefined;
  const localAnimationData = useMemo(() => {
    return animationUrl ? null : isValidLottiePayload(animation);
  }, [animation, animationUrl]);

  const [animationData, setAnimationData] = useState<object | null>(localAnimationData);
  const [isReady, setIsReady] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(true);

  useEffect(function checkReducedMotion() {
    const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

    if (reducedMotionQuery.matches) {
      setShouldAnimate(false);
    }
  }, []);

  useEffect(() => {
    if (!animationUrl) {
      setAnimationData(localAnimationData);
    }
  }, [animationUrl, localAnimationData]);

  useEffect(
    function fetchAnimation() {
      if (localAnimationData || !animationUrl || !shouldAnimate) {
        return;
      }

      fetch(buildProxyUrl(animationUrl))
        .then(function (res) {
          return res.json();
        })
        .then(function (data) {
          setAnimationData(isValidLottiePayload(data));
        })
        .catch(function (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          setAnimationData(null);
        });
    },
    [animationUrl, localAnimationData, shouldAnimate],
  );

  return {
    animationData,
    isReady,
    shouldAnimate,
    setIsReady,
  };
}
